import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { OpenAPI } from "@/client/core/OpenAPI"
import type { TopicSuggestionsResponse } from "@/hooks/useTopicSuggestions"

function apiBaseUrl(): string {
  const base = (OpenAPI.BASE || import.meta.env.VITE_API_URL || "").replace(
    /\/$/,
    "",
  )
  if (!base) {
    throw new Error(
      "API base URL is not configured. Set VITE_API_URL to your backend origin.",
    )
  }
  return base
}

function authHeaders(): Record<string, string> {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  }
}

async function getProposals(status: string): Promise<TopicSuggestionsResponse> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/ai/topic-proposals?status=${status}`,
    { headers: authHeaders() },
  )
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.detail || "Failed to load topic proposals")
  }
  return response.json()
}

async function postAction(suggestionId: string, action: string): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/ai/topic-proposals/${suggestionId}/${action}`,
    { method: "POST", headers: authHeaders() },
  )
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.detail || `Failed to ${action} proposal`)
  }
}

export function useTopicProposals(status = "proposed") {
  return useQuery({
    queryKey: ["topic-proposals", status],
    queryFn: () => getProposals(status),
  })
}

export function useApproveTopicProposal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => postAction(id, "approve"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["topic-proposals"] }),
  })
}

export function useDismissTopicProposal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => postAction(id, "dismiss"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["topic-proposals"] }),
  })
}
